import { useEffect, useState, Fragment, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { I18n } from 'aws-amplify'

import SensorEnrollmentsListItem from './SensorEnrollmentsListItem'

import { Cell, Column, Row, Slot, Table, Loader, Box } from '@/primitives'

import { Pagination, SearchBarWithDebounce } from '@/elements'

import { showBanner } from '@/slices/util'

import { getSensorEnrollments } from '@/api/management/sensorEnrollment'

import Strings from '../Strings'

const SensorEnrollmentsList = ({ refreshTrigger }) => {
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [enrollments, setEnrollments] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch()

  const strings = Strings()

  const onUpdateSearchText = useCallback(text => {
    setSearchText(text)
    setPage(1)
  }, [])

  const fetchEnrollments = useCallback(async () => {
    setIsLoading(true)
    try {
      const { enrollments: data, error } = await getSensorEnrollments({
        page,
        pageSize,
        search: searchText
      })

      if (error) {
        dispatch(
          showBanner({
            show: true,
            message: error,
            type: 'error'
          })
        )
        setEnrollments([])
        setTotalItems(0)
      } else {
        setEnrollments(data?.result || [])
        setTotalItems(data?.totalItems || 0)
      }
    } catch (err) {
      dispatch(
        showBanner({
          show: true,
          message: err.message || I18n.get('An error occurred'),
          type: 'error'
        })
      )
      setEnrollments([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, page, pageSize, searchText])

  useEffect(() => {
    fetchEnrollments()
  }, [fetchEnrollments, refreshTrigger])

  return (
    <Fragment>
      <SearchBarWithDebounce
        isLoading={isLoading}
        onUpdateSearchText={onUpdateSearchText}
        placeholderText={I18n.get('Search by ID or organization')}
      />
      <Loader
        isLoading={isLoading}
        text={I18n.get('Loading sensor enrollments')}
      >
        <Table style={{ marginTop: '1rem' }}>
          <Slot name='head'>
            <Row>
              <Column>{I18n.get('ID')}</Column>
              <Column>{I18n.get('Organization')}</Column>
              <Column>{I18n.get('Role')}</Column>
              <Column>{I18n.get('Devices')}</Column>
              <Column>{I18n.get('Sign-ups')}</Column>
              <Column>{strings.tableActionsColumn}</Column>
            </Row>
          </Slot>

          <Slot name='body'>
            {!isLoading && enrollments?.length === 0 && (
              <Row>
                <Cell colSpan='6'>
                  {I18n.get('No sensor enrollments found')}
                </Cell>
              </Row>
            )}
            {enrollments?.map(enrollment => (
              <SensorEnrollmentsListItem
                key={enrollment.id}
                enrollment={enrollment}
                onRefresh={fetchEnrollments}
              />
            ))}
          </Slot>
        </Table>
        {totalItems > pageSize && (
          <Box className='pagination-menu'>
            <Pagination
              type='simple'
              totalItems={totalItems}
              totalPages={totalPages}
              setTotalPages={setTotalPages}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </Box>
        )}
      </Loader>
    </Fragment>
  )
}

export default SensorEnrollmentsList
